import React, { useEffect, useMemo, useState } from 'react';
import 'react-international-phone/style.css';
import BaseCard from '@/components/ui/BaseCard.tsx';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import AccordionSection from '@/components/ui/AccordionSection.tsx';
import OpportunityMainDetailsSection from '@/features/opportunities/form/OpportunityMainDetailsSection.tsx';
import OpportunityFormActions from '@/features/opportunities/form/OpportunityFormActions.tsx';
import OpportunityStatusAlert from '@/features/opportunities/form/OpportunityStatusAlert.tsx';
import { CreateOpportunityRequest, InvestmentType, OpportunityDTO } from '@inversor-hub/types';
import {
  useCreateOpportunity,
  useDeleteOpportunityMedia,
  useGetOpportunityMedia,
  useUpdateOpportunity,
  useUploadOpportunityMedia,
  usePublishOpportunity,
  useHideOpportunity,
  useShowOpportunity,
} from '@/lib/api/opportunities.ts';
import TernaryOptionSelect from '@/components/form/TernaryOptionSelect.tsx';
import ComboField from '@/components/form/ComboField.tsx';
import NumberField from '@/components/form/NumberField.tsx';
import StarField from '@/components/form/StarsField.tsx';
import { OpportunityFormData } from '@/features/opportunities/form/types.ts';
import { toOpportunityFormData, toOpportunityUpdate } from '@/features/opportunities/form/utils.ts';
import OpportunityMonthlyRentSection from '@/features/opportunities/form/OpportunityMonthlyRentSection.tsx';
import OpportunityTransactionDetailsSection from '@/features/opportunities/form/OpportunityTransactionDetailsSection.tsx';
import { GoogleMapsProvider } from '@/context/GoogleMapsProvider.tsx';
import OpportunityPreview from '@/features/opportunities/OpportunityPreview.tsx';
import { Link, useNavigate } from 'react-router-dom';
import DropzoneUploader from '@/components/form/DropzoneUploader';
import useSnackbar from '@/hooks/useSnackbar.tsx';

const initialFormData: OpportunityFormData = {
  title: '',
  description: '',
  type: 'TRADITIONAL_RENT' as InvestmentType,
  address: '',
  postalCode: '',
  city: '',
  province: '',
  locationDescription: '',
  showApproximateAddress: false,
  cadastralReference: '',
  observations: '',
  sellers: '',
  itePassed: '',
  itePassedObservations: '',
  buildingYear: '',
  houseOrientation: '',
  buildingFloor: '',
  rooms: 0,
  bathrooms: 0,
  squareMeters: 0,
  electricityCondition: 0,
  plumbingCondition: 0,
  streetCondition: 0,
  facadeCondition: 0,
  shuttersCondition: null,
  stairCondition: null,
  terraceCondition: null,
  electricityRegistration: '',
  electricityRegistrationObservations: '',
  waterRegistration: '',
  waterRegistrationObservations: '',
  vacantSale: '',
  vacantSaleObservations: '',
  hasElevator: '',
  hasElevatorObservations: '',
  energyCertificate: '',
  energyCertificateObservations: '',
  debtCertificate: '',
  debtCertificateObservations: '',
  simpleCopy: '',
  simpleCopyObservations: '',
  vpo: '',
  vpoObservations: '',
  inheritance: '',
  inheritanceObservations: '',
  communityFee: null,
  propertyTaxIbi: null,
  totalCommunityExpenses: null,
  wasteTax: null,
  nonNegotiatedPrice: null,
  negotiatedPrice: null,
  purchaseTaxItp: null,
  purchaseExpenses: null,
  realtorCommission: null,
  psiCommission: null,
  expectedMonthlyRent: null,
  roomRent: null,
  recurringIncome: null,
  deedAmount: null,
  referenceValue: null,
  rentDefaultInsurance: null,
  homeInsurance: null,
  otherExpenses: null,
  notaryExpenses: null,
  registryExpenses: null,
  renovationCost: null,
  estimatedSalePrice: null,
  grossReturnRental: null,
  grossReturnRooms: null,
  netReturnRooms: null,
  netReturnRental: null,
  grossReturnFlipping: null,
  netReturnFlipping: null,
  isPrivate: false,
  investorId: '',
};

const floorOptions = [
  'Planta Baja',
  'Entresuelo',
  'Planta 1',
  'Planta 2',
  'Planta 3',
  'Planta 4',
  'Planta 5',
  'Planta 6',
  'Planta 7',
];
const orientationOptions = [
  'Norte',
  'Sur',
  'Este',
  'Oeste',
  'Noreste',
  'Sureste',
  'Noroeste',
  'Suroeste',
];

interface Props {
  mode: 'create' | 'update';
  opportunity?: OpportunityDTO;
}

const OpportunityForm = ({ mode, opportunity }: Props) => {
  const { show: showSnackbar } = useSnackbar();
  const [openHideConfirmDialog, setOpenHideConfirmDialog] = useState(false);
  const { mutateAsync: createOpportunity } = useCreateOpportunity();
  const { mutateAsync: updateOpportunity } = useUpdateOpportunity();
  const { mutateAsync: uploadOpportunityMedia } = useUploadOpportunityMedia();
  const { mutateAsync: publishOpportunity } = usePublishOpportunity();
  const { mutateAsync: hideOpportunity } = useHideOpportunity();
  const { mutateAsync: showOpportunity } = useShowOpportunity();

  const { data: media } = useGetOpportunityMedia(opportunity?.id);
  const { mutateAsync: deleteOpportunityMedia } = useDeleteOpportunityMedia();
  const images = useMemo(
    () =>
      media
        ?.filter((m) => m.type === 'IMAGE')
        .map((m) => ({
          id: m.id,
          type: m.mimeType,
          url: m.signedUrl,
          name: m.fileName,
          isMain: m.id === opportunity?.mainImageId && m.mimeType.startsWith('image/'),
        })) ?? [],
    [media, opportunity?.mainImageId]
  );
  const documents = useMemo(
    () =>
      media
        ?.filter((m) => m.type === 'DOCUMENT')
        .map((m) => ({
          id: m.id,
          type: m.mimeType,
          url: m.signedUrl,
          name: m.fileName,
        })) ?? [],
    [media]
  );

  const navigate = useNavigate();

  const [formData, setFormData] = useState<OpportunityFormData>(initialFormData);

  const canPublish = opportunity?.status === 'DRAFT';

  const yearOptions: string[] = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: currentYear - 1919 }, (_, index) =>
      (1920 + index).toString()
    ).reverse();
  }, []);

  useEffect(() => {
    if (!opportunity) {
      return;
    }
    setFormData(toOpportunityFormData(opportunity));
  }, [opportunity?.id]);

  const handleChangeField = (id: string, value: number | string | null) => {
    setFormData({ ...formData, [id]: value });
  };

  const handleChangeFields = (changeset: Record<string, number | string | null>) => {
    setFormData({ ...formData, ...changeset });
  };

  const handleMainDetailsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newOpportunityParams: CreateOpportunityRequest = {
      ...formData,
      addressInfo: {
        address: formData.address,
        postalCode: formData.postalCode,
        city: formData.city,
        province: formData.province,
        locationDescription: formData.locationDescription,
        showApproximateAddress: formData.showApproximateAddress,
      },
      type: formData.type,
    };

    try {
      const newOpportunity = await createOpportunity(newOpportunityParams);
      navigate(`/oportunidades/${newOpportunity.id}/editar`);
    } catch (error) {
      console.error('Error al guardar el documento: ', error);
    }
  };

  const handleOpportunityDataSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const updateRequest = toOpportunityUpdate(formData);

    try {
      await updateOpportunity({ changeset: updateRequest, id: opportunity!.id });
      showSnackbar({ title: 'Los cambios se han guardado con éxito.', variant: 'success' });
    } catch (error) {
      console.error('Error al guardar el documento: ', error);
    }
  };

  const handlePublishOpportunity = async () => {
    if (!opportunity) return;

    const updateRequest = toOpportunityUpdate(formData);
    try {
      await updateOpportunity({ changeset: updateRequest, id: opportunity!.id });
      await publishOpportunity(opportunity.id);
      showSnackbar({ title: 'La oportunidad se ha publicado para revisión.', variant: 'success' });
    } catch (error) {
      console.error('Error al publicar la oportunidad: ', error);
    }
  };

  const handleHideOpportunity = async () => {
    if (!opportunity) return;
    setOpenHideConfirmDialog(false);

    try {
      await hideOpportunity(opportunity.id);
      showSnackbar({
        title: 'La oportunidad se ha ocultado.',
        message: 'Ya no será visible para los inversores',
        variant: 'info',
      });
    } catch (error) {
      console.error('Error al ocultar la oportunidad: ', error);
    }
  };

  const handleHideButtonClick = () => {
    setOpenHideConfirmDialog(true);
  };

  const handleShowOpportunity = async () => {
    if (!opportunity) return;

    try {
      await showOpportunity(opportunity.id);
      showSnackbar({ title: 'La oportunidad se ha mostrado.', variant: 'success' });
    } catch (error) {
      console.error('Error al mostrar la oportunidad: ', error);
    }
  };

  const [expanded, setExpanded] = useState([true, ...Array(8).fill(false)]);

  const handleChangeAccordion = (index: number) => {
    setExpanded((prevExpanded) => prevExpanded.map((item, i) => (i === index ? !item : item)));
  };

  const handleNavigateToMonthlyRent = () => {
    setExpanded((prevExpanded) => prevExpanded.map((item, i) => (i === 4 ? true : item)));

    setTimeout(() => {
      const monthlyRentSection = document.querySelector('[data-section="monthly-rent"]');
      if (monthlyRentSection) {
        const elementTop = monthlyRentSection.getBoundingClientRect().top + window.pageYOffset;
        const offset = 80; // Offset to show section a bit higher on screen

        window.scrollTo({
          top: elementTop - offset,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  return (
    <GoogleMapsProvider>
      <Box
        sx={{
          width: '100%',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          bgcolor: 'background.default',
          py: { xs: 2, md: 4 },
          px: { xs: 0, sm: 2, md: 0 },
          overflowX: 'hidden',
        }}
      >
        <BaseCard
          title="Alta Oportunidad"
          sx={{
            width: '100%',
            maxWidth: { xs: '100vw', md: 900 },
            mx: 'auto',
            boxShadow: 3,
            p: { xs: 0.5, sm: 2, md: 3 },
            overflow: 'hidden',
          }}
        >
          <Box sx={{ width: '100%' }}>
            {opportunity && (
              <OpportunityStatusAlert
                status={opportunity.status}
                reviewMessage={opportunity.reviewMessage}
              />
            )}
            <AccordionSection
              title={'Datos Generales'}
              disabled={false}
              expanded={expanded[0]}
              handleToggleAccordion={() => handleChangeAccordion(1)}
            >
              <Box sx={{ width: '100%' }}>
                <OpportunityMainDetailsSection
                  formData={formData}
                  setFormData={setFormData}
                  submitted={mode === 'update'}
                  handleChangeField={handleChangeField}
                  handleSubmit={handleMainDetailsSubmit}
                />
              </Box>
            </AccordionSection>

            <br></br>
            <form
              name="opportunityDetails"
              onSubmit={handleOpportunityDataSubmit}
              style={{ width: '100%' }}
            >
              <AccordionSection
                title={'Características'}
                disabled={mode === 'create'}
                expanded={expanded[1]}
                handleToggleAccordion={() => handleChangeAccordion(1)}
              >
                <Stack spacing={3} sx={{ width: '100%' }}>
                  <Box mb={0} width="100%">
                    <TernaryOptionSelect
                      label="¿El edificio ha pasado la ITE?"
                      id="itePassed"
                      name="itePassed"
                      optionValue={formData.itePassed}
                      observationsValue={formData.itePassedObservations}
                      onOptionChange={handleChangeField}
                      onObservationsChange={(value) =>
                        handleChangeField('itePassedObservations', value)
                      }
                    />
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: 'Arial, sans-serif', fontSize: 11 }}
                    >
                      (Solo obligatorio si tiene más de 50 años - en Elche, en otros municipios
                      puede ser 45 años. Se puede consultar con los vecinos o con el administrador
                      de fincas)
                    </Typography>
                  </Box>
                  <Box mb={0} width="100%">
                    <ComboField
                      value={formData.houseOrientation}
                      onChange={handleChangeField}
                      options={orientationOptions}
                      label="¿Orientación de la vivienda?"
                      name="houseOrientation"
                      id="houseOrientation"
                    />
                    <Box width="100%">
                      <Typography
                        variant="body1"
                        sx={{ fontFamily: 'Arial, sans-serif', fontSize: 11 }}
                      >
                        <Link to="https://www.api.cat/noticias/cual-es-la-mejor-orientacion-para-una-vivienda/">
                          https://www.api.cat/noticias/cual-es-la-mejor-orientacion-para-una-vivienda/
                        </Link>
                      </Typography>
                    </Box>
                  </Box>

                  <ComboField
                    value={formData.buildingYear}
                    onChange={handleChangeField}
                    options={yearOptions}
                    label="¿Año de construcción?"
                    name="buildingYear"
                    id="buildingYear"
                  />

                  <ComboField
                    value={formData.buildingFloor}
                    onChange={handleChangeField}
                    options={floorOptions}
                    label="¿Qué planta es?"
                    name="buildingFloor"
                    id="buildingFloor"
                  />
                  <NumberField
                    id="rooms"
                    name="rooms"
                    label="¿Cuántas habitaciones tiene? (Después de reforma si la hubiera)"
                    variant="outlined"
                    min={1}
                    max={6}
                    decimals={0}
                    value={formData.rooms}
                    onChange={handleChangeField}
                  />
                  <NumberField
                    id="bathrooms"
                    name="bathrooms"
                    label="¿Cuántos baños tiene? (Después de reforma si la hubiera)"
                    variant="outlined"
                    min={1}
                    max={4}
                    decimals={0}
                    value={formData.bathrooms}
                    onChange={handleChangeField}
                  />
                  <Box mb={0} width="100%">
                    <NumberField
                      id="squareMeters"
                      name="squareMeters"
                      label="¿Cuántos metros tiene?"
                      variant="outlined"
                      value={formData.squareMeters}
                      onChange={handleChangeField}
                    />
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: 'Arial, sans-serif', fontSize: 11 }}
                    >
                      <Link to="https://www.sedecatastro.gob.es/Accesos/SECAccInmuebles.aspx">
                        https://www.sedecatastro.gob.es/Accesos/SECAccInmuebles.aspx
                      </Link>
                    </Typography>
                  </Box>
                </Stack>
              </AccordionSection>

              <AccordionSection
                title={'Conservación'}
                disabled={mode === 'create'}
                expanded={expanded[2]}
                handleToggleAccordion={() => handleChangeAccordion(2)}
              >
                <Stack spacing={3} sx={{ width: '100%' }}>
                  <StarField
                    onChange={handleChangeField}
                    id="electricityCondition"
                    labelLeft="Muy desmejorada"
                    labelRight="En perfecto estado"
                    label={
                      '¿Estado de la electricidad? (observa enchufes, llaves de la luz, automáticos)'
                    }
                    value={formData.electricityCondition}
                  />
                  <StarField
                    onChange={handleChangeField}
                    id="plumbingCondition"
                    labelLeft="Muy desmejorada"
                    labelRight="En perfecto estado"
                    label={'¿Estado de la fontanería? (observa grifos, tipo de tuberías, cisterna)'}
                    value={formData.plumbingCondition}
                  />
                  <StarField
                    onChange={handleChangeField}
                    id="streetCondition"
                    labelLeft="Muy desmejorada"
                    labelRight="En perfecto estado"
                    label={
                      'Estado de la calle (observa la limpieza, vecindario, seguridad, aceras, elementos de ruido)'
                    }
                    value={formData.streetCondition}
                  />
                  <StarField
                    onChange={handleChangeField}
                    id="facadeCondition"
                    labelLeft="Muy desmejorada"
                    labelRight="En perfecto estado"
                    label={
                      'Estado de conservación de la fachada (observa el material, si tiene desconchados, suciedad)'
                    }
                    value={formData.facadeCondition}
                  />
                  <StarField
                    onChange={handleChangeField}
                    id="shuttersCondition"
                    labelLeft="Muy desmejorado"
                    labelRight="En perfecto estado"
                    nullable
                    label={
                      'Estado de conservación del postigo (observa suciedad, pintura, buzones, humedades, contadores)'
                    }
                    value={formData.shuttersCondition}
                  />
                  <StarField
                    onChange={handleChangeField}
                    id="stairCondition"
                    labelLeft="Muy desmejorada"
                    labelRight="En perfecto estado"
                    label={
                      'Estado de conservación de la escalera (observa la suciedad, desgaste de pasamanos, pintura de paredes, estado de los escalones)'
                    }
                    nullable
                    value={formData.stairCondition}
                  />
                  <StarField
                    onChange={handleChangeField}
                    id="terraceCondition"
                    labelLeft="Muy desmejorada"
                    labelRight="En perfecto estado"
                    nullable
                    label={
                      'Estado de la terraza (importante sobre todo si estamos comprando en la última planta)'
                    }
                    value={formData.terraceCondition}
                  />
                </Stack>
              </AccordionSection>

              <AccordionSection
                title={'Checklist'}
                disabled={mode === 'create'}
                expanded={expanded[3]}
                handleToggleAccordion={() => handleChangeAccordion(3)}
              >
                <Stack spacing={3}>
                  <Box mb={0} width="100%">
                    <TernaryOptionSelect
                      label="¿La luz está dada de alta?"
                      id="electricityRegistration"
                      name="electricityRegistration"
                      optionValue={formData.electricityRegistration}
                      observationsValue={formData.electricityRegistrationObservations}
                      onOptionChange={handleChangeField}
                      onObservationsChange={(textValue) =>
                        handleChangeField('electricityRegistrationObservations', textValue)
                      }
                    />
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: 'Arial, sans-serif', fontSize: 11 }}
                    >
                      (puede requerir de un boletín)
                    </Typography>
                  </Box>
                  <Box mb={0} width="100%">
                    <TernaryOptionSelect
                      label="¿El agua está dada de alta?"
                      id="waterRegistration"
                      name="waterRegistration"
                      optionValue={formData.waterRegistration}
                      observationsValue={formData.waterRegistrationObservations}
                      onOptionChange={handleChangeField}
                      onObservationsChange={(value) =>
                        handleChangeField('waterRegistrationObservations', value)
                      }
                    />
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: 'Arial, sans-serif', fontSize: 11 }}
                    >
                      (puede requerir de unos arreglos para poner el contador + cédula de 2ª
                      ocupación)
                    </Typography>
                  </Box>
                  <Box mb={0} width="100%">
                    <TernaryOptionSelect
                      label="¿Se vende vacío?"
                      id="vacantSale"
                      name="vacantSale"
                      optionValue={formData.vacantSale}
                      observationsValue={formData.vacantSaleObservations}
                      onOptionChange={handleChangeField}
                      onObservationsChange={(value) =>
                        handleChangeField('vacantSaleObservations', value)
                      }
                    />
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: 'Arial, sans-serif', fontSize: 11 }}
                    >
                      (si no se vende vacío es importante saber qué
                      muebles/enseres/electrodomésticos se quedan y hacer una lista de todo lo que
                      se queda y adjuntarla al contrato de arras).
                    </Typography>
                  </Box>
                  <Box mb={0} width="100%">
                    <TernaryOptionSelect
                      label="¿Tiene ascensor?"
                      id="hasElevator"
                      name="hasElevator"
                      optionValue={formData.hasElevator}
                      observationsValue={formData.hasElevatorObservations}
                      onOptionChange={handleChangeField}
                      onObservationsChange={(value) =>
                        handleChangeField('hasElevatorObservations', value)
                      }
                    />
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: 'Arial, sans-serif', fontSize: 11 }}
                    >
                      (En caso de no tener, está previsto poner uno?. Sólo obligatorio si tiene más
                      de 50 años - en Elche, en otros municipios puede ser 45 años. Se puede
                      consultar con los vecinos o con el administrador de fincas)
                    </Typography>
                  </Box>
                  <Box mb={0} width="100%">
                    <TernaryOptionSelect
                      label="¿La vivienda tiene Certificado de Eficiencia Energética?"
                      id="energyCertificate"
                      name="energyCertificate"
                      optionValue={formData.energyCertificate}
                      observationsValue={formData.energyCertificateObservations}
                      onOptionChange={handleChangeField}
                      onObservationsChange={(value) =>
                        handleChangeField('energyCertificateObservations', value)
                      }
                    />
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: 'Arial, sans-serif', fontSize: 11 }}
                    >
                      (El vendedor tiene que proporcionarlo antes de escriturar)
                    </Typography>
                  </Box>
                  <Box mb={0} width="100%">
                    <TernaryOptionSelect
                      label="¿Se ha solicitado un certificado de deuda al administrador de fincas?"
                      id="debtCertificate"
                      name="debtCertificate"
                      optionValue={formData.debtCertificate}
                      observationsValue={formData.debtCertificateObservations}
                      onOptionChange={handleChangeField}
                      onObservationsChange={(value) =>
                        handleChangeField('debtCertificateObservations', value)
                      }
                    />
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: 'Arial, sans-serif', fontSize: 11 }}
                    >
                      (Es necesario para conocer si hay alguna deuda o derrama pendiente. Se suele
                      hacer la semana anterior a la firma, pero es importante llamar y solicitar
                      información al respecto.)
                    </Typography>
                  </Box>
                  <Box mb={0} width="100%">
                    <TernaryOptionSelect
                      label="¿Se ha solicitado una nota simple?"
                      id="simpleCopy"
                      name="simpleCopy"
                      optionValue={formData.simpleCopy}
                      observationsValue={formData.simpleCopyObservations}
                      onOptionChange={handleChangeField}
                      onObservationsChange={(value) =>
                        handleChangeField('simpleCopyObservations', value)
                      }
                    />
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: 'Arial, sans-serif', fontSize: 11 }}
                    >
                      (Es necesario para conocer si hay cargas y cuántos vendedores. Hay que fijarse
                      si alguno de los vendedores ha fallecido o si poseen el usufructo/nuda
                      propiedad. Si la vivienda está hipotecada, es necesario conocer la deuda y
                      hacer el acto de levantamiento antes o durante la compraventa. Puede conllevar
                      tiempos adicionales.) https://sede.registradores.org/site/home
                    </Typography>
                  </Box>
                  <Box mb={0} width="100%">
                    <TernaryOptionSelect
                      label="¿La vivienda es de VPO?"
                      id="vpo"
                      name="vpo"
                      optionValue={formData.vpo}
                      observationsValue={formData.vpoObservations}
                      onOptionChange={handleChangeField}
                      onObservationsChange={(value) => handleChangeField('vpoObservations', value)}
                    />
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: 'Arial, sans-serif', fontSize: 11 }}
                    >
                      (Lo pone en la nota simple. Ojo, porque las viviendas de VPO tienen su
                      burocracia.)
                    </Typography>
                  </Box>
                  <Box mb={0} width="100%">
                    <TernaryOptionSelect
                      label="¿La vivienda proviene de herencia?"
                      id="inheritance"
                      name="inheritance"
                      optionValue={formData.inheritance}
                      observationsValue={formData.inheritanceObservations}
                      onOptionChange={handleChangeField}
                      onObservationsChange={(value) =>
                        handleChangeField('inheritanceObservations', value)
                      }
                    />
                    <Typography
                      variant="body1"
                      sx={{ fontFamily: 'Arial, sans-serif', fontSize: 11 }}
                    >
                      (De ser así, ¿hay testamento? ¿declaración de herederos? Puede conllevar
                      tiempos adicionales)
                    </Typography>
                  </Box>
                </Stack>
              </AccordionSection>

              <AccordionSection
                title={'Flujo mensual'}
                disabled={mode === 'create'}
                expanded={expanded[4]}
                handleToggleAccordion={() => handleChangeAccordion(4)}
                data-section="monthly-rent"
              >
                <OpportunityMonthlyRentSection
                  formData={formData}
                  handleChangeField={handleChangeField}
                />
              </AccordionSection>

              <AccordionSection
                title={'Detalles de transacción'}
                disabled={mode === 'create'}
                expanded={expanded[5]}
                handleToggleAccordion={() => handleChangeAccordion(5)}
              >
                <OpportunityTransactionDetailsSection
                  formData={formData}
                  handleChangeField={handleChangeField}
                  handleChangeFields={handleChangeFields}
                  onNavigateToMonthlyRent={handleNavigateToMonthlyRent}
                  opportunity={opportunity}
                />
              </AccordionSection>

              <AccordionSection
                title={'Fotos/Videos'}
                disabled={mode === 'create'}
                expanded={expanded[6]}
                handleToggleAccordion={() => handleChangeAccordion(6)}
              >
                <div>
                  <DropzoneUploader
                    existingFiles={images}
                    handleUploadFile={async (file) =>
                      uploadOpportunityMedia({
                        file,
                        opportunityId: opportunity!.id,
                        fileType: 'IMAGE',
                      })
                    }
                    maxFiles={30}
                    uploadType="images"
                    onDeleteExistingFile={(id) => deleteOpportunityMedia(id)}
                    onSetMainImage={async (mediaId) => {
                      await updateOpportunity({
                        id: opportunity!.id,
                        changeset: {
                          mainImageId: mediaId,
                        },
                      });
                    }}
                  />
                </div>
              </AccordionSection>

              <AccordionSection
                title={'Documentación'}
                disabled={mode === 'create'}
                expanded={expanded[7]}
                handleToggleAccordion={() => handleChangeAccordion(7)}
              >
                <div>
                  <Box mb={2}>
                    <Alert severity="info" sx={{ alignItems: 'center' }}>
                      Los documentos subidos aquí son <b>privados</b> y solo visibles para ti y el
                      inversor que trabaje contigo en esta operación. Úsala como gestor documental.
                    </Alert>
                  </Box>
                  <DropzoneUploader
                    handleUploadFile={async (file) =>
                      uploadOpportunityMedia({
                        file,
                        opportunityId: opportunity!.id,
                        fileType: 'DOCUMENT',
                      })
                    }
                    onDeleteExistingFile={(id) => deleteOpportunityMedia(id)}
                    existingFiles={documents}
                    uploadType="documents"
                  />
                </div>
              </AccordionSection>

              {opportunity && (
                <AccordionSection
                  title={'Preview'}
                  disabled={mode === 'create'}
                  expanded={expanded[8]}
                  handleToggleAccordion={() => handleChangeAccordion(8)}
                >
                  <OpportunityPreview
                    data={formData}
                    opportunityId={opportunity.id}
                    mainImageId={opportunity.mainImageId}
                  />
                </AccordionSection>
              )}

              <br />
              {mode === 'update' && opportunity && (
                <OpportunityFormActions
                  status={opportunity.status}
                  canPublish={canPublish}
                  onPublish={handlePublishOpportunity}
                  onSave={handleOpportunityDataSubmit}
                  onHide={handleHideButtonClick}
                  onShow={handleShowOpportunity}
                  reviewMessage={opportunity.reviewMessage}
                />
              )}
            </form>
            <Dialog open={openHideConfirmDialog} onClose={() => setOpenHideConfirmDialog(false)}>
              <DialogTitle>¿Ocultar esta oportunidad?</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Dejará de ser visible para los inversores. No se perderán los datos.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenHideConfirmDialog(false)}>Cancelar</Button>
                <Button onClick={handleHideOpportunity} color="error" autoFocus>
                  Ocultar
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </BaseCard>
      </Box>
    </GoogleMapsProvider>
  );
};

export default OpportunityForm;
